import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { TypedMemo } from '../../../common/types';
import { useVideoCredits } from './useVideoCredits';
import { useVideoCreditGate } from './useVideoCreditGate';
import { creditBlockView } from './creditBlockView';
import { creditMessages } from './creditMessages';
import { CreditSplit } from './CreditSplit';
import { CreditCostCell } from './CreditCostCell';
import { CreditHaveCell } from './CreditHaveCell';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the credit block; each part uses its own slice of it */
import styles from './StudioCreditBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface StudioCreditBlockProps {
  accountId: number;
  /** The credits of this make: 3 for a full video, 1 for a short video, 0 for an image. */
  cost: number;
  /** True for an asset that needs a paid plan (a full video, the motion artwork). */
  needsPlan?: boolean;
  /** False hides the "This needs 3 credits" note, for example while "Make it" is off for another reason. */
  showShortNote?: boolean;
  /** The test id of the "This needs 3 credits" note. */
  shortNoteTestId?: string;
}

/**
 * The credit block of the Studio rail (design release-kit t5). It replaces the g7 box. Two numbers
 * side by side: "This costs" and "You have" (plan credits left plus pack credits, one number).
 * "How? Plan and packs" opens the split. When the balance is too low, "You have" turns red and a
 * note says what is missing. The block reads the same credits answer as `useVideoCreditGate`, so
 * the block and the pre-click check always agree. It shows nothing while the credits load.
 */
const StudioCreditBlockComponent: React.FC<StudioCreditBlockProps> = (props) => {
  const { accountId, cost, needsPlan = false, showShortNote = true, shortNoteTestId } = props;
  const intl = useIntl();
  const { credits } = useVideoCredits({ accountId });
  const gate = useVideoCreditGate(accountId);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);

  if (credits === null) {
    return null;
  }

  const view = creditBlockView(credits, cost, needsPlan);
  // An unlimited plan (limit null) has no split (design t5).
  const hasSplit = credits.limit !== null;

  return (
    <>
      <Panel nested className={styles.block} data-testid="credit-block">
        <CreditCostCell cost={cost} view={view} />
        <CreditHaveCell credits={credits} view={view} />
        {hasSplit && (
          <button
            type="button"
            className={styles.how}
            aria-expanded={isOpen}
            onClick={toggle}
            data-testid="credit-block-how"
          >
            <span>{intl.formatMessage(creditMessages.blockHow)}</span>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className={styles.chevron} />
          </button>
        )}
        {hasSplit && isOpen && <CreditSplit credits={credits} view={view} />}
      </Panel>
      {view.isShort && showShortNote && (
        <span className={clsx('small', styles.warn)} data-testid={shortNoteTestId}>
          {gate.shortNote(cost)}
        </span>
      )}
    </>
  );
};

export const StudioCreditBlock = TypedMemo(StudioCreditBlockComponent);
